"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@stockline/ui/components/alert-dialog";
import { AlertTriangle } from "lucide-react";
import type {
  ConfirmWarning,
  WarningCategory,
} from "../../services/confirmWarnings";

interface ConfirmWarningsDialogProps {
  readonly isOpen: boolean;
  readonly warnings: readonly ConfirmWarning[];
  readonly onClose: () => void;
  readonly onContinue: () => void;
}

const CATEGORY_ORDER: WarningCategory[] = [
  "required",
  "zeroQuantity",
  "zeroPrice",
];

const CATEGORY_HEADING_KEY: Record<WarningCategory, string> = {
  required: "ordersRecap.warningRequiredHeading",
  zeroQuantity: "ordersRecap.warningZeroQuantityHeading",
  zeroPrice: "ordersRecap.warningZeroPriceHeading",
};

// Standard (non-custom) field keys → their i18n label.
const STANDARD_FIELD_KEY: Record<string, string> = {
  quantity: "ordersRecap.quantity",
  weight: "ordersRecap.weight",
  unit_id: "ordersRecap.unit",
  price_per_weight: "ordersRecap.price",
};

export function ConfirmWarningsDialog({
  isOpen,
  warnings,
  onClose,
  onContinue,
}: ConfirmWarningsDialogProps) {
  const { t } = useTranslation();

  const groups = useMemo(() => {
    return CATEGORY_ORDER.map((category) => {
      const items = warnings
        .filter((w) => w.category === category)
        .map((w) => ({
          lineNumber: w.lineNumber,
          label: w.custom
            ? w.fieldLabel
            : t(STANDARD_FIELD_KEY[w.fieldKey] ?? w.fieldKey),
        }))
        .sort((a, b) => a.lineNumber - b.lineNumber);
      return { category, items };
    }).filter((g) => g.items.length > 0);
  }, [warnings, t]);

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent
        data-testid="confirm-warnings-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t("ordersRecap.confirmWarningsTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("ordersRecap.confirmWarningsDescription")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {groups.map(({ category, items }) => (
            <div key={category}>
              <p className="text-sm font-semibold text-foreground">
                {t(CATEGORY_HEADING_KEY[category])}
              </p>
              <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                {items.map((it, i) => (
                  <li key={`${it.lineNumber}-${i}`}>
                    <span className="font-medium text-foreground">
                      {t("ordersRecap.lineLabel", { number: it.lineNumber })}
                    </span>
                    {` : ${it.label}`}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {t("ordersRecap.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-warnings-continue-button"
            onClick={onContinue}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {t("ordersRecap.confirmAnyway")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
